import { BLACK, WHITE, opposite, type Color } from '../primitives/color.js'
import {
  EMPTY,
  KING,
  PAWN,
  codeType,
  decodePiece,
  encodePiece,
  type Piece,
  type PieceCode,
} from '../primitives/piece.js'
import { BOARD_SIZE, sq, type Square } from '../primitives/square.js'
import { isPawnMove, type Move } from './move.js'

export const CASTLE_WK = 1
export const CASTLE_WQ = 2
export const CASTLE_BK = 4
export const CASTLE_BQ = 8
export const CASTLE_ALL = CASTLE_WK | CASTLE_WQ | CASTLE_BK | CASTLE_BQ

/** Bitfield over the four `CASTLE_*` flags. */
export type CastlingRights = number

/**
 * A complete chess position — everything FEN encodes, plus a cached king square
 * per colour so check tests do not have to scan for the king.
 *
 * `board` is a `Uint8Array` of packed piece codes indexed by 0x88 square. It is
 * typed as a plain array rather than something exotically readonly because
 * every hot path in movegen indexes it; treat any `Position` you receive as
 * frozen and go through `makeMove`, which clones.
 */
export interface Position {
  readonly board: Uint8Array
  readonly turn: Color
  readonly castling: CastlingRights
  /**
   * Set after any double pawn push, whether or not a capture is actually
   * available — that is what FEN writes, so it is what round-trips. Repetition
   * comparison narrows this to "capturable only"; see `game/repetition.ts`.
   */
  readonly epSquare: Square | null
  readonly halfmoves: number
  readonly fullmoves: number
  readonly kings: readonly [Square, Square]
}

/** The same shape with the writes unlocked. Only `makeMoveInPlace` gets one. */
export interface MutablePosition {
  board: Uint8Array
  turn: Color
  castling: CastlingRights
  epSquare: Square | null
  halfmoves: number
  fullmoves: number
  kings: [Square, Square]
}

/** Everything `makeMoveInPlace` destroys, so `undoMoveInPlace` can put it back. */
export interface Undo {
  readonly capturedCode: PieceCode
  readonly capturedSquare: Square
  readonly castling: CastlingRights
  readonly epSquare: Square | null
  readonly halfmoves: number
  readonly kingSquare: Square
}

/**
 * Castling rights that survive a piece arriving at or departing from each
 * square. Applying it to both `from` and `to` handles all three ways rights
 * die — the king moves, the rook moves, or the rook is captured where it
 * stands — without a single special case.
 */
const CASTLE_MASK = ((): Uint8Array => {
  const mask = new Uint8Array(BOARD_SIZE).fill(CASTLE_ALL)
  mask[sq('a1')] = CASTLE_ALL & ~CASTLE_WQ
  mask[sq('h1')] = CASTLE_ALL & ~CASTLE_WK
  mask[sq('e1')] = CASTLE_ALL & ~(CASTLE_WK | CASTLE_WQ)
  mask[sq('a8')] = CASTLE_ALL & ~CASTLE_BQ
  mask[sq('h8')] = CASTLE_ALL & ~CASTLE_BK
  mask[sq('e8')] = CASTLE_ALL & ~(CASTLE_BK | CASTLE_BQ)
  return mask
})()

export const codeAt = (pos: Position, square: Square): PieceCode => pos.board[square] as PieceCode
export const pieceAt = (pos: Position, square: Square): Piece | null => decodePiece(codeAt(pos, square))

export const clonePosition = (pos: Position): MutablePosition => ({
  board: pos.board.slice(),
  turn: pos.turn,
  castling: pos.castling,
  epSquare: pos.epSquare,
  halfmoves: pos.halfmoves,
  fullmoves: pos.fullmoves,
  kings: [pos.kings[0], pos.kings[1]],
})

/**
 * Mutates in place and returns the undo record. This is the single
 * implementation of chess movement — `makeMove` below is a clone away from it,
 * and perft runs on it directly, so the fast path and the pure path can never
 * drift apart.
 */
export const makeMoveInPlace = (pos: MutablePosition, move: Move): Undo => {
  const { board } = pos
  const us = pos.turn
  const moving = board[move.from] as PieceCode

  // For every move except en passant the captured piece stands on the
  // destination. En passant is the one case where it does not, which is exactly
  // why it breaks naive engines.
  const capturedSquare =
    move.kind === 'enPassant' ? (((us === WHITE ? move.to - 16 : move.to + 16) as Square)) : move.to
  const capturedCode = board[capturedSquare] as PieceCode

  const undo: Undo = {
    capturedCode,
    capturedSquare,
    castling: pos.castling,
    epSquare: pos.epSquare,
    halfmoves: pos.halfmoves,
    kingSquare: pos.kings[us],
  }

  if (move.kind === 'enPassant') board[capturedSquare] = EMPTY
  board[move.from] = EMPTY
  board[move.to] = move.kind === 'promotion' ? encodePiece(us, move.promoteTo) : moving

  if (move.kind === 'castle') {
    board[move.rookTo] = board[move.rookFrom] as PieceCode
    board[move.rookFrom] = EMPTY
  }

  if (codeType(moving) === KING) pos.kings[us] = move.to

  pos.castling &= (CASTLE_MASK[move.from] as number) & (CASTLE_MASK[move.to] as number)
  // A double push's from and to are 32 apart, so their midpoint is the square
  // it skipped over.
  pos.epSquare = move.kind === 'doublePush' ? (((move.from + move.to) >> 1) as Square) : null
  pos.halfmoves = isPawnMove(move) || capturedCode !== EMPTY ? 0 : pos.halfmoves + 1
  if (us === BLACK) pos.fullmoves += 1
  pos.turn = opposite(us)

  return undo
}

export const undoMoveInPlace = (pos: MutablePosition, move: Move, undo: Undo): void => {
  const { board } = pos
  const us = opposite(pos.turn)

  pos.turn = us
  if (us === BLACK) pos.fullmoves -= 1
  pos.castling = undo.castling
  pos.epSquare = undo.epSquare
  pos.halfmoves = undo.halfmoves
  pos.kings[us] = undo.kingSquare

  board[move.from] =
    move.kind === 'promotion' ? encodePiece(us, PAWN) : (board[move.to] as PieceCode)
  board[move.to] = EMPTY

  if (move.kind === 'castle') {
    board[move.rookFrom] = board[move.rookTo] as PieceCode
    board[move.rookTo] = EMPTY
  }

  if (undo.capturedCode !== EMPTY) board[undo.capturedSquare] = undo.capturedCode
}

/** The pure front door: clone, mutate the clone, hand it back. */
export const makeMove = (pos: Position, move: Move): Position => {
  const next = clonePosition(pos)
  makeMoveInPlace(next, move)
  return next
}

export const emptyPosition = (): MutablePosition => ({
  board: new Uint8Array(BOARD_SIZE),
  turn: WHITE,
  castling: 0,
  epSquare: null,
  halfmoves: 0,
  fullmoves: 1,
  kings: [sq('e1'), sq('e8')],
})
